import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, map, Observable, Subscription, tap } from 'rxjs';
import { BillingService } from 'src/app/_services/billing.service';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import { ProductService } from 'src/app/_services/product.service';
import {
  BillState,
  FlowInvoiceInsight,
  FlowInvoicesFilters,
  InsurerDto,
  InvoiceDocument,
} from 'src/app/_types/billing.types';
import { BillStateComboboxComponent } from 'src/app/Dashboard/billing/bill-state-combobox/bill-state-combobox.component';
import { ComboboxComponent } from 'src/app/shared/components/combobox/combobox.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';

import { TaskDetailsService } from '../../task-details.service';
import { FlowInvoicesGridContext } from './flow-invoices-grid-context';
import { FlowInvoicesTableComponent } from './flow-invoices-table/flow-invoices-table.component';

/** Every bill state, in the order the filter dropdown should offer them. */
const STATUS_FILTER_STATES: BillState[] = [
  'TO_BE_VALIDATED',
  'PENDING',
  'SENT',
  'REJECTED',
];

/** Local calendar date as `YYYY-MM-DD` (never UTC — that shifts the day). */
function toIsoDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** One insurance flow's billing items, rendered as its own list. */
type FlowInvoiceGroup = {
  flowId: string;
  items: FlowInvoiceInsight[];
};

/**
 * Splits the flat response into one group per insurance flow — the route
 * returns the items of every flow sharing this flow's display id, so a single
 * list would mix several flows together.
 *
 * The backend's order is preserved throughout (reception date descending, then
 * kind, then id), which is what keeps a reminder next to the invoice it chases;
 * `Map` iterates in insertion order, so the groups come out newest-first too.
 * Each grid stays independently sortable by clicking its headers.
 */
function buildGroups(items: FlowInvoiceInsight[]): FlowInvoiceGroup[] {
  const byFlow = new Map<string, FlowInvoiceInsight[]>();

  for (const item of items) {
    const existing = byFlow.get(item.insuranceFlowId);
    if (existing) existing.push(item);
    else byFlow.set(item.insuranceFlowId, [item]);
  }

  return Array.from(byFlow.entries()).map(([flowId, flowItems]) => ({
    flowId,
    items: flowItems,
  }));
}

/**
 * "Facturation" section of the flow detail modal: every invoice of every
 * proposal of every flow sharing this flow's display id.
 *
 * The route is unpaginated and unsorted, so unlike the billing list there is no
 * infinite scroll and ag-grid sorts client-side; filtering stays server-side.
 */
@Component({
  selector: 'app-flow-invoices',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    ExpansionPanelComponent,
    ComboboxComponent,
    BillStateComboboxComponent,
    IconsModule,
    FlowInvoicesTableComponent,
  ],
  templateUrl: './flow-invoices.component.html',
  styleUrls: ['./flow-invoices.component.css'],
  // Cell styles must reach the AG Grid DOM, which lives outside this component
  encapsulation: ViewEncapsulation.None,
})
export class FlowInvoicesComponent implements OnInit, OnDestroy {
  private readonly taskDetailsService = inject(TaskDetailsService);

  private readonly insuranceFlowService = inject(InsuranceFlowService);

  private readonly billingService = inject(BillingService);

  private readonly productService = inject(ProductService);

  private readonly toastr = inject(ToastrService);

  readonly groups = signal<FlowInvoiceGroup[]>([]);

  readonly loading = signal(false);

  private readonly flowId = signal<string | null>(null);

  private stateSubscription?: Subscription;

  private previousIsLoading = true;

  readonly statusFilterStates = STATUS_FILTER_STATES;

  readonly searchControl = new FormControl('');

  readonly insurerFilter = new FormControl<InsurerDto | null>(null);

  readonly statusFilter = new FormControl<BillState | null>(null);

  readonly billingDateFilter = new FormControl('');

  readonly periodRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  private readonly allInsurers = signal<InsurerDto[]>([]);

  private readonly insurerQuery = signal('');

  readonly insurerSearchResults = computed(() => {
    const query = this.insurerQuery().trim().toLowerCase();
    const insurers = this.allInsurers();
    if (!query) return insurers;
    return insurers.filter((insurer) =>
      insurer.name.toLowerCase().includes(query)
    );
  });

  readonly gridContext: FlowInvoicesGridContext = {
    downloadDocument: (item) => this.downloadDocument(item),
  };

  ngOnInit(): void {
    this.stateSubscription = this.taskDetailsService.state$.subscribe(
      (state) => {
        const flowId = state.insuranceFlowData?.id ?? null;
        const shouldReload = this.previousIsLoading && !state.isLoading;
        this.previousIsLoading = state.isLoading;

        this.flowId.set(flowId);
        if (!flowId || !shouldReload) return;

        this.fetch();
      }
    );

    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => this.fetch());
    this.insurerFilter.valueChanges.subscribe(() => this.fetch());
    this.statusFilter.valueChanges.subscribe(() => this.fetch());
    this.billingDateFilter.valueChanges.subscribe(() => this.fetch());

    this.productService.searchInsurers('').subscribe({
      next: (res) => this.allInsurers.set(res.data?.insurers ?? []),
      error: () => this.allInsurers.set([]),
    });
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
  }

  /** Drives the single-label empty state of the period filter. */
  protected get isPeriodEmpty(): boolean {
    const { start, end } = this.periodRange.value;
    return !start && !end;
  }

  onInsurerSearch(query: string): void {
    this.insurerQuery.set(query);
    if (!query.trim() && this.insurerFilter.value !== null) {
      this.insurerFilter.setValue(null);
    }
  }

  /** Refetch only on a complete range change, so a half-typed range is ignored. */
  onPeriodChange(): void {
    const { start, end } = this.periodRange.value;
    if (!!start === !!end) this.fetch();
  }

  resetPeriod(): void {
    this.periodRange.reset();
    this.fetch();
  }

  private fetch(): void {
    const flowId = this.flowId();
    if (!flowId) return;

    this.loading.set(true);
    this.insuranceFlowService
      .getFlowInvoices(flowId, this.buildFilters())
      .subscribe({
        next: (res) => {
          this.groups.set(buildGroups(res.data?.items ?? []));
          this.loading.set(false);
        },
        error: () => {
          this.groups.set([]);
          this.loading.set(false);
          this.toastr.error('Erreur lors du chargement des factures');
        },
      });
  }

  private buildFilters(): FlowInvoicesFilters {
    const status = this.statusFilter.value;
    const { start, end } = this.periodRange.value;

    return {
      search: this.searchControl.value?.trim() || undefined,
      categoryId: this.insurerFilter.value?.insurerId || undefined,
      status: status ? [status] : undefined,
      billingDate: this.billingDateFilter.value || undefined,
      periodFrom: start ? toIsoDate(start) : undefined,
      periodTo: end ? toIsoDate(end) : undefined,
    };
  }

  /**
   * The list payload carries only `hasDocument`; the presigned link lives on
   * the item's detail and expires, so it is resolved at click time — from
   * `/reminder/{id}` or `/invoice/{id}` depending on the row's kind.
   */
  private downloadDocument(
    item: FlowInvoiceInsight
  ): Observable<InvoiceDocument | null> {
    // Mapped inside each branch: `.pipe` cannot resolve against a union of two
    // differently-typed Observables.
    const document$: Observable<InvoiceDocument | null> =
      item.kind === 'REMINDER'
        ? this.billingService
            .getReminderDetail(item.id)
            .pipe(map((response) => response.data?.document ?? null))
        : this.billingService
            .getInvoiceDetail(item.id)
            .pipe(map((response) => response.data?.document ?? null));

    return document$.pipe(
      tap({
        next: (itemDocument) => {
          if (!itemDocument?.url) {
            this.toastr.error('Aucun document disponible pour cette ligne');
            return;
          }
          this.openDocument(
            itemDocument.url,
            itemDocument.filename ?? item.documentFilename
          );
        },
        error: () =>
          this.toastr.error('Erreur lors du téléchargement du document'),
      })
    );
  }

  /**
   * Presigned S3 links are cross-origin, so browsers ignore the `download`
   * attribute and open the file instead — which is an acceptable fallback.
   */
  private openDocument(url: string, filename?: string | null): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    if (filename) link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
